import { useEffect, useState } from "react";
import AppShell, { SectionCard, StatCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, PencilSimple, Trash, LockSimple, CurrencyInr, Sparkle, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";
import IncomeTaxTab from "./IncomeTaxTab";
import { Textarea } from "../components/ui/textarea";
import PayrollSetupWizard from "../components/PayrollSetupWizard";
import { DateInput } from "../components/ui/date-input";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function Payroll() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "setup");
  return (
    <AppShell title="Payroll" moduleKey="payroll">
      <div className="flex items-center gap-1 mb-5 border-b border-zinc-200 overflow-x-auto">
        {[
          {k:"setup",l:"Getting started"},
          {k:"components",l:"Components"},
          {k:"structures",l:"Salary structures"},
          {k:"compensations",l:"Employee CTC"},
          {k:"income_tax",l:"Income Tax"},
        ].map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)}
            data-testid={`pay-tab-${t.k}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 whitespace-nowrap ${tab===t.k?"border-zinc-950 text-zinc-950 font-medium":"border-transparent text-zinc-500 hover:text-zinc-900"}`}>{t.l}</button>
        ))}
      </div>
      {tab === "setup" && <SetupGuideTab onNavigate={setTab}/>}
      {tab === "components" && <ComponentsTab/>}
      {tab === "structures" && <StructuresTab/>}
      {tab === "compensations" && <CompensationsTab/>}
      {tab === "income_tax" && <IncomeTaxTab/>}
    </AppShell>
  );
}


function SetupGuideTab({ onNavigate }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/payroll/setup-guide/status");
      setState(r.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (!state) return <div className="text-sm text-zinc-500 p-6">{busy ? "Loading setup status…" : ""}</div>;

  const gotoTab = (href) => {
    const q = new URLSearchParams((href.split("?")[1]) || "");
    const tab = q.get("tab");
    if (tab && onNavigate) onNavigate(tab);
    else window.location.href = href;
  };

  return (
    <div className="space-y-4">
      <SectionCard
        title="Payroll setup progress"
        subtitle={`${state.done} of ${state.total} steps complete · ${state.pct}% ready`}
        testid="setup-guide"
      >
        <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden mb-4">
          <div className="h-full bg-emerald-500 transition-all" style={{width: `${state.pct}%`}}/>
        </div>

        <div className="grid gap-3">
          {state.steps.map((s, i) => (
            <div key={s.key}
              className={`flex items-start gap-3 rounded-md border p-4 ${s.done ? "border-emerald-200 bg-emerald-50/40" : "border-zinc-200 bg-white"}`}
              data-testid={`setup-step-${s.key}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-medium text-sm ${s.done ? "bg-emerald-500 text-white" : "bg-zinc-200 text-zinc-700"}`}>
                {s.done ? "✓" : i+1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">{s.title}</span>
                  {s.total !== undefined && (
                    <Badge variant="outline" className="text-[10px]">
                      {s.count}/{s.total}{s.pct !== undefined ? ` · ${s.pct}%` : ""}
                    </Badge>
                  )}
                  {s.total === undefined && s.count > 0 && (
                    <Badge variant="outline" className="text-[10px]">{s.count}</Badge>
                  )}
                </div>
                <div className="text-xs text-zinc-500">{s.description}</div>
              </div>
              <Button size="sm" variant={s.done ? "ghost" : "outline"}
                onClick={() => gotoTab(s.action_href)}
                data-testid={`setup-action-${s.key}`}>
                {s.action_label}
              </Button>
            </div>
          ))}
        </div>

        {state.ready_to_run_payroll ? (
          <div className="mt-4 rounded-md border border-emerald-300 bg-emerald-50 p-4 text-sm">
            <div className="font-medium text-emerald-900 mb-1">✅ You're ready to run payroll!</div>
            <div className="text-emerald-800 text-xs mb-3">Go to Payroll Runs and click <b>New run</b> for the target month.</div>
            <Button size="sm" onClick={() => window.location.href = "/app/payroll-runs"} data-testid="setup-goto-runs">
              Go to Payroll Runs
            </Button>
          </div>
        ) : (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm">
            <div className="font-medium text-amber-900 mb-1">⚠️ Complete the required steps first</div>
            <div className="text-amber-800 text-xs">Components, structures, employee CTCs and bank details are required to run payroll.</div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function ComponentsTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const load = () => api.get("/salary-components").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const blank = () => ({ name:"", code:"", kind:"earning", category:"basic",
    calculation_type:"fixed", default_value:0, is_taxable:true, is_in_ctc:true,
    is_pf_applicable:false, is_esic_applicable:false, is_pt_applicable:false,
    display_on_payslip:true, hra_exempt_sec10:false, lta_exempt:false, sort_order:10 });
  const save = async () => {
    try {
      if (f.id) await api.put(`/salary-components/${f.id}`, f);
      else await api.post("/salary-components", f);
      toast.success("Saved"); setOpen(false); setF(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Disable component?")) return; await api.delete(`/salary-components/${id}`); toast.success("Disabled"); load(); };

  return (
    <SectionCard title={`${rows.length} component${rows.length === 1 ? "" : "s"}`} subtitle="Earnings, deductions, and employer-cost line items. Statutory ones are locked." testid="section-components"
      action={<Button size="sm" className="gap-1.5" onClick={()=>{ setF(blank()); setOpen(true); }} data-testid="add-comp-btn"><Plus size={14} weight="bold"/> Add component</Button>}
    >
      <Table>
        <TableHeader><TableRow>
          <TableHead>Component</TableHead><TableHead>Kind</TableHead><TableHead>Calculation</TableHead>
          <TableHead>Taxable</TableHead><TableHead>PF</TableHead><TableHead>ESIC</TableHead><TableHead/>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map(c => (
            <TableRow key={c.id} data-testid={`comp-row-${c.code}`}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{c.name}</span>
                  <Badge variant="outline" className="text-[10px]">{c.code}</Badge>
                  {c.is_locked && <LockSimple size={12} className="text-zinc-400" title="Statutory — locked"/>}
                </div>
              </TableCell>
              <TableCell><Badge variant="outline" className={`text-[10px] uppercase ${c.kind==='earning'?'bg-emerald-50 border-emerald-200 text-emerald-700':c.kind==='deduction'?'bg-red-50 border-red-200 text-red-700':'bg-zinc-50'}`}>{c.kind}</Badge></TableCell>
              <TableCell className="text-xs">
                {c.calculation_type === "pct_of_ctc" && `${c.default_value}% of CTC`}
                {c.calculation_type === "pct_of_basic" && `${c.default_value}% of Basic`}
                {c.calculation_type === "pct_of_gross" && `${c.default_value}% of Gross`}
                {c.calculation_type === "fixed" && `₹${c.default_value}/yr`}
                {c.calculation_type === "fixed_monthly" && `₹${c.default_value}/mo`}
                {c.calculation_type === "statutory" && `Statutory (${c.default_value}%)`}
              </TableCell>
              <TableCell>{c.is_taxable ? "Yes" : "No"}</TableCell>
              <TableCell>{c.is_pf_applicable ? "Yes" : "—"}</TableCell>
              <TableCell>{c.is_esic_applicable ? "Yes" : "—"}</TableCell>
              <TableCell className="text-right">
                {!c.is_locked && (
                  <>
                    <Button size="sm" variant="outline" onClick={()=>{ setF({...c}); setOpen(true); }}><PencilSimple size={14}/></Button>
                    <Button size="sm" variant="ghost" className="text-red-600 ml-1" onClick={()=>del(c.id)}><Trash size={14}/></Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={v=>{ setOpen(v); if (!v) setF(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{f?.id ? "Edit component" : "New component"}</DialogTitle></DialogHeader>
          {f && <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input className="mt-1" value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="comp-name"/></div>
              <div><Label>Code</Label><Input className="mt-1" value={f.code} onChange={e=>setF({...f,code:e.target.value.toUpperCase()})} maxLength={10} data-testid="comp-code"/></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Kind</Label>
                <Select value={f.kind} onValueChange={v=>setF({...f,kind:v})}>
                  <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="earning">Earning</SelectItem><SelectItem value="deduction">Deduction</SelectItem><SelectItem value="employer_cost">Employer Cost</SelectItem></SelectContent>
                </Select></div>
              <div><Label>Calculation</Label>
                <Select value={f.calculation_type} onValueChange={v=>setF({...f,calculation_type:v})}>
                  <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed (₹/year)</SelectItem>
                    <SelectItem value="fixed_monthly">Fixed (₹/month)</SelectItem>
                    <SelectItem value="pct_of_ctc">% of CTC</SelectItem>
                    <SelectItem value="pct_of_basic">% of Basic</SelectItem>
                    <SelectItem value="pct_of_gross">% of Gross</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label>Default value</Label><Input type="number" step="0.01" className="mt-1" value={f.default_value} onChange={e=>setF({...f,default_value:Number(e.target.value)})}/></div>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              {[
                ["is_taxable","Taxable"],["is_in_ctc","Part of CTC"],
                ["is_pf_applicable","Counts toward PF wages"],["is_esic_applicable","Counts toward ESIC"],
                ["is_pt_applicable","Counts toward PT"],["display_on_payslip","Show on payslip"],
                ["hra_exempt_sec10","HRA exempt (Sec 10(13A))"],["lta_exempt","LTA exempt"],
              ].map(([k,lbl])=>(
                <div key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" id={`comp-flag-${k}`} aria-label={lbl} checked={!!f[k]}
                    onChange={e=>setF({...f,[k]:e.target.checked})} data-testid={`comp-flag-${k}`}/>
                  <label htmlFor={`comp-flag-${k}`}>{lbl}</label>
                </div>
              ))}
            </div>
          </div>}
          <DialogFooter><Button variant="ghost" onClick={()=>{ setOpen(false); setF(null); }}>Cancel</Button><Button onClick={save} data-testid="comp-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

function StructuresTab() {
  const [rows, setRows] = useState([]);
  const [components, setComponents] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);

  const load = async () => {
    const [s, c] = await Promise.all([
      api.get("/salary-structures"),
      api.get("/salary-components"),
    ]);
    setRows(s.data);
    setComponents(c.data || []);
    // Auto-open the wizard once, if the tenant is empty
    if ((s.data || []).length === 0 && (c.data || []).length === 0 && !autoOpened) {
      setWizardOpen(true);
      setAutoOpened(true);
    }
  };
  useEffect(() => { load(); }, []);

  const blank = () => ({
    name: "",
    description: "",
    applies_to_grades: [],
    target_ctc_annual: 600000,
    lines: [],
  });

  const addLine = () => {
    const used = new Set((f.lines || []).map(l => l.component_id));
    const first = (components || []).find(c => !used.has(c.id));
    if (!first) { toast.error("No more components available — add new components first."); return; }
    const calc = first.calculation_type === "statutory" ? "statutory"
                : first.code === "BASIC" ? "pct_of_ctc"
                : first.kind === "earning" ? "pct_of_basic"
                : "fixed";
    setF({
      ...f,
      lines: [...(f.lines || []), {
        component_id: first.id,
        component_code: first.code,
        component_name: first.name,
        calculation_type: calc,
        value: first.code === "BASIC" ? 40 : 0,
      }],
    });
  };

  const setLine = (idx, patch) => {
    const lines = [...f.lines];
    if (patch.component_id) {
      const c = components.find(x => x.id === patch.component_id);
      if (c) {
        patch.component_code = c.code;
        patch.component_name = c.name;
        if (c.calculation_type === "statutory") patch.calculation_type = "statutory";
      }
    }
    lines[idx] = { ...lines[idx], ...patch };
    setF({ ...f, lines });
  };

  const removeLine = (idx) => setF({ ...f, lines: f.lines.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!f.name?.trim()) return toast.error("Name is required");
    if (!(f.lines || []).length) return toast.error("Add at least one component line");
    try {
      if (f.id) await api.put(`/salary-structures/${f.id}`, f);
      else await api.post("/salary-structures", f);
      toast.success(f.id ? "Structure updated" : "Structure created");
      setOpen(false); setF(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Disable this structure? Assigned employees keep their CTC.")) return;
    try {
      await api.delete(`/salary-structures/${id}`);
      toast.success("Structure disabled"); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed"); }
  };

  const calcOptions = [
    { v: "pct_of_ctc",    label: "% of CTC" },
    { v: "pct_of_basic",  label: "% of Basic" },
    { v: "pct_of_gross",  label: "% of Gross" },
    { v: "fixed",         label: "Fixed (₹/year)" },
    { v: "fixed_monthly", label: "Fixed (₹/month)" },
    { v: "balance",       label: "Balance (auto-fill remainder)" },
    { v: "statutory",     label: "Statutory (PF/ESI/PT)" },
  ];

  return (
    <SectionCard
      title={`${rows.length} salary structure${rows.length === 1 ? "" : "s"}`}
      subtitle="Reusable CTC templates per grade/band. Assign to new hires to auto-fill their compensation."
      testid="section-structures"
      action={
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" data-testid="wizard-btn"
                  onClick={() => setWizardOpen(true)}>
            <Sparkle size={14} weight="fill"/> Guided setup
          </Button>
          <Button size="sm" className="gap-1.5" data-testid="add-structure-btn"
                  onClick={() => { setF(blank()); setOpen(true); }}>
            <Plus size={14} weight="bold"/> Add structure
          </Button>
        </div>
      }
    >
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-500 py-10 text-center border border-dashed rounded-lg" data-testid="structures-empty">
          <Sparkle size={28} weight="fill" className="mx-auto mb-2 text-amber-500"/>
          <div className="text-base text-zinc-800 font-medium mb-1">Let&apos;s get your payroll ready in 60 seconds.</div>
          <div className="text-xs text-zinc-500 mb-3">
            Not sure where to start? Use guided setup — we&apos;ll create components + your first structure for you.
          </div>
          <Button size="sm" className="gap-1.5" onClick={() => setWizardOpen(true)} data-testid="wizard-empty-btn">
            <Sparkle size={14} weight="fill"/> Start guided setup
          </Button>
          <div className="text-[11px] text-zinc-400 mt-2">Or click <b>Add structure</b> to do it manually.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map(s => (
            <div key={s.id} className="bg-white border border-zinc-200 rounded-lg p-5" data-testid={`struct-card-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{s.applies_to_grades?.join(" · ") || "All grades"}</div>
                  <div className="font-display font-semibold truncate">{s.name}</div>
                  <div className="text-xs text-zinc-500 mt-1">Target CTC: {inr(s.target_ctc_annual)}/year</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`struct-edit-${s.id}`}
                          onClick={() => { setF({ ...s }); setOpen(true); }}>
                    <PencilSimple size={14}/>
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" data-testid={`struct-del-${s.id}`}
                          onClick={() => del(s.id)}>
                    <Trash size={14}/>
                  </Button>
                </div>
              </div>
              <div className="mt-3 divide-y divide-zinc-100 text-xs">
                {(s.lines || []).slice(0, 6).map(l => (
                  <div key={l.component_code} className="flex items-center justify-between py-1.5">
                    <span className="text-zinc-600">{l.component_name}</span>
                    <span className="font-mono-alt">
                      {l.calculation_type === "fixed" ? `₹${l.value}` :
                       l.calculation_type === "pct_of_ctc" ? `${l.value}% CTC` :
                       l.calculation_type === "pct_of_basic" ? `${l.value}% Basic` :
                       l.calculation_type === "balance" ? "Balance" :
                       "Statutory"}
                    </span>
                  </div>
                ))}
                {s.lines?.length > 6 && <div className="text-zinc-400 py-1">+ {s.lines.length - 6} more…</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── Payroll Setup Wizard ─── */}
      <PayrollSetupWizard open={wizardOpen} onOpenChange={setWizardOpen} onComplete={load}/>

      {/* ─── Add / Edit Structure Dialog ─── */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setF(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="struct-dialog">
          <DialogHeader>
            <DialogTitle>{f?.id ? "Edit salary structure" : "New salary structure"}</DialogTitle>
          </DialogHeader>
          {f && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              {!f.id && (
                <AIStructureShortcuts
                  components={components}
                  onApply={(draft) => {
                    // Merge the AI draft into the current form. Preserve any
                    // manual edits the user has already made (name/desc).
                    const nextLines = (draft.lines || []).map(l => {
                      const comp = components.find(c => c.code === l.component_code);
                      return {
                        component_id: comp?.id || "",
                        calculation_type: l.calculation_type || "fixed_monthly",
                        value: Number(l.value) || 0,
                      };
                    }).filter(l => l.component_id);
                    setF(prev => ({
                      ...prev,
                      name: prev.name || draft.name || "",
                      description: prev.description || draft.description || "",
                      target_ctc_annual: draft.target_ctc_annual || prev.target_ctc_annual,
                      applies_to_grades: draft.applies_to_grades?.length ? draft.applies_to_grades : prev.applies_to_grades,
                      lines: nextLines.length ? nextLines : prev.lines,
                    }));
                    if (draft.ai_notes) toast.success(`AI: ${draft.ai_notes.slice(0,120)}…`, { duration: 5000 });
                  }}
                />
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input data-testid="struct-name" value={f.name}
                         onChange={(e) => setF({ ...f, name: e.target.value })}
                         placeholder="e.g. Default FTE — Bangalore"/>
                </div>
                <div>
                  <Label className="text-xs">Target CTC (₹/year)</Label>
                  <Input data-testid="struct-ctc" type="number" value={f.target_ctc_annual}
                         onChange={(e) => setF({ ...f, target_ctc_annual: parseFloat(e.target.value) || 0 })}/>
                </div>
              </div>
              <div>
                <Label className="text-xs">Applies to grades (comma-separated, blank = all)</Label>
                <Input data-testid="struct-grades"
                       value={(f.applies_to_grades || []).join(", ")}
                       onChange={(e) => setF({ ...f, applies_to_grades: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                       placeholder="M1, M2, M3"/>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input data-testid="struct-desc" value={f.description || ""}
                       onChange={(e) => setF({ ...f, description: e.target.value })}
                       placeholder="Optional"/>
              </div>

              {/* Lines */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Components ({(f.lines || []).length})</Label>
                  <Button size="sm" variant="outline" className="gap-1.5 h-8"
                          onClick={addLine} data-testid="struct-add-line">
                    <Plus size={12}/> Add line
                  </Button>
                </div>

                {(f.lines || []).length === 0 ? (
                  <div className="text-xs text-zinc-500 text-center py-6 border border-dashed rounded">
                    No components yet. Click <b>Add line</b> to start.
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="struct-lines">
                    {f.lines.map((ln, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`struct-line-${i}`}>
                        <div className="col-span-5">
                          <Select value={ln.component_id} onValueChange={(v) => setLine(i, { component_id: v })}>
                            <SelectTrigger className="h-9" data-testid={`struct-line-comp-${i}`}><SelectValue/></SelectTrigger>
                            <SelectContent>
                              {components.map(c => (
                                <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-4">
                          <Select value={ln.calculation_type} onValueChange={(v) => setLine(i, { calculation_type: v })}>
                            <SelectTrigger className="h-9" data-testid={`struct-line-calc-${i}`}><SelectValue/></SelectTrigger>
                            <SelectContent>
                              {calcOptions.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Input type="number" value={ln.value} className="h-9"
                                 disabled={ln.calculation_type === "statutory" || ln.calculation_type === "balance"}
                                 onChange={(e) => setLine(i, { value: parseFloat(e.target.value) || 0 })}
                                 data-testid={`struct-line-value-${i}`}/>
                        </div>
                        <div className="col-span-1 text-right">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600"
                                  onClick={() => removeLine(i)} data-testid={`struct-line-del-${i}`}>
                            <Trash size={14}/>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
                  Tip: Build it in this order — <b>Basic</b> as % of CTC (typically 40%) →
                  <b> HRA</b> as % of Basic (50%) → other allowances (fixed or %) →
                  <b> Special Allowance</b> as <i>Balance</i> to fill the remainder →
                  statutory components (PF, ESI, PT) auto-calculated by Indian rules.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setF(null); }}>Cancel</Button>
            <Button onClick={save} data-testid="struct-save">{f?.id ? "Save changes" : "Create structure"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

function CompensationsTab() {
  const [rows, setRows] = useState([]);
  const [structures, setStructures] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee_id:"", structure_id:"", ctc_annual:0, effective_from:"", tax_regime:"new", revised_reason:"" });
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const [a, st, e] = await Promise.all([
      api.get("/compensation/all"),
      api.get("/salary-structures"),
      api.get("/employees"),
    ]);
    setRows(a.data); setStructures(st.data); setEmployees(e.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const { data } = await api.post("/compensation/assign", f);
      toast.success("Compensation assigned");
      setPreview(data);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const totals = {
    assigned: rows.length,
    totalCTC: rows.reduce((a, r) => a + (r.ctc_annual || 0), 0),
    unassigned: employees.filter(e => !rows.find(r => r.employee_id === e.id)).length,
  };

  return (
    <>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Employees with CTC" value={totals.assigned} testid="stat-assigned"/>
        <StatCard label="Total CTC / year" value={inr(totals.totalCTC)} testid="stat-totalctc"/>
        <StatCard label="Unassigned" value={totals.unassigned} testid="stat-unassigned"/>
      </div>

      <SectionCard title="Employee compensation" subtitle="One current CTC per employee. Revisions create a new version with effective_from." testid="section-compensations"
        action={<Dialog open={open} onOpenChange={v=>{setOpen(v); if(!v){setF({employee_id:"",structure_id:"",ctc_annual:0,effective_from:"",tax_regime:"new",revised_reason:""}); setPreview(null);}}}>
          <DialogTrigger asChild><Button size="sm" className="gap-1.5" data-testid="assign-btn"><Plus size={14} weight="bold"/> Assign CTC</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Assign / revise compensation</DialogTitle></DialogHeader>
            {!preview && <div className="space-y-3 py-2">
              <div><Label>Employee</Label>
                <Select value={f.employee_id} onValueChange={v=>setF({...f,employee_id:v})}>
                  <SelectTrigger className="mt-1" data-testid="assign-emp"><SelectValue placeholder="Pick"/></SelectTrigger>
                  <SelectContent>{employees.map(e=><SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Structure (template)</Label>
                  <Select value={f.structure_id} onValueChange={v=>setF({...f,structure_id:v})}>
                    <SelectTrigger className="mt-1" data-testid="assign-struct"><SelectValue placeholder="Default"/></SelectTrigger>
                    <SelectContent>{structures.map(s=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>CTC (annual ₹)</Label>
                  <Input type="number" step="1000" className="mt-1" value={f.ctc_annual} onChange={e=>setF({...f,ctc_annual:Number(e.target.value)})} data-testid="assign-ctc"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Effective from</Label><DateInput className="mt-1" value={f.effective_from} onChange={e=>setF({...f,effective_from:e.target.value})} data-testid="assign-effective"/></div>
                <div><Label>Tax regime</Label>
                  <Select value={f.tax_regime} onValueChange={v=>setF({...f,tax_regime:v})}>
                    <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="new">New regime (default 2020+)</SelectItem><SelectItem value="old">Old regime</SelectItem></SelectContent>
                  </Select></div>
              </div>
              <div><Label>Revision reason (optional)</Label><Input className="mt-1" value={f.revised_reason} onChange={e=>setF({...f,revised_reason:e.target.value})} placeholder="Appraisal / promotion / market correction"/></div>
            </div>}

            {preview && <CompensationBreakdown sal={preview}/>}

            <DialogFooter>
              {!preview && <><Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button onClick={submit} data-testid="assign-submit">Assign</Button></>}
              {preview && <Button onClick={()=>setOpen(false)}>Done</Button>}
            </DialogFooter>
          </DialogContent>
        </Dialog>}
      >
        <Table>
          <TableHeader><TableRow>
            <TableHead>Employee</TableHead><TableHead>Structure</TableHead><TableHead>CTC/yr</TableHead>
            <TableHead>Gross/mo</TableHead><TableHead>Net/mo</TableHead><TableHead>Regime</TableHead><TableHead>From</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-zinc-500">No CTCs assigned yet.</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`comp-row-${r.employee_id}`}>
                <TableCell><div className="font-medium">{r.employee_name}</div><div className="text-xs text-zinc-500">{r.employee_code}</div></TableCell>
                <TableCell className="text-xs">{r.structure_name || "Custom"}</TableCell>
                <TableCell className="font-mono-alt">{inr(r.ctc_annual)}</TableCell>
                <TableCell className="font-mono-alt">{inr(r.gross_monthly)}</TableCell>
                <TableCell className="font-mono-alt font-medium">{inr(r.net_monthly_estimate)}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px] uppercase">{r.tax_regime}</Badge></TableCell>
                <TableCell className="text-xs">{r.effective_from}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>
    </>
  );
}

function CompensationBreakdown({ sal }) {
  const earnings = sal.lines.filter(l => l.kind === "earning");
  const deductions = sal.lines.filter(l => l.kind === "deduction");
  const employer = sal.lines.filter(l => l.kind === "employer_cost");
  return (
    <div className="py-2 space-y-4" data-testid="comp-preview">
      <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4">
        <div className="tiny-label">Monthly summary</div>
        <div className="grid grid-cols-3 gap-4 mt-2">
          <div><div className="text-xs text-zinc-500">Gross</div><div className="font-display font-bold text-xl">{inr(sal.gross_monthly)}</div></div>
          <div><div className="text-xs text-zinc-500">Deductions</div><div className="font-display font-bold text-xl text-red-600">-{inr(deductions.reduce((a,d)=>a+d.monthly_amount,0))}</div></div>
          <div><div className="text-xs text-zinc-500">Net estimate</div><div className="font-display font-bold text-xl text-emerald-600">{inr(sal.net_monthly_estimate)}</div></div>
        </div>
      </div>
      <Breakdown title="Earnings" lines={earnings} color="text-emerald-700"/>
      <Breakdown title="Deductions" lines={deductions} color="text-red-600"/>
      {employer.length > 0 && <Breakdown title="Employer contributions (not in take-home)" lines={employer} color="text-zinc-500" small/>}
    </div>
  );
}

function Breakdown({ title, lines, color, small }) {
  if (lines.length === 0) return null;
  return (
    <div>
      <div className={`tiny-label mb-2 ${color}`}>{title}</div>
      <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-md">
        {lines.map(l => (
          <div key={l.component_code} className="flex items-center justify-between px-3 py-2 text-sm">
            <div className="flex items-center gap-2">
              <span>{l.component_name}</span>
              <Badge variant="outline" className="text-[10px]">{l.component_code}</Badge>
            </div>
            <div className={`font-mono-alt ${small?"text-zinc-500":""}`}>{inr(l.monthly_amount)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ─── AI shortcuts for new salary structure ─── */
function AIStructureShortcuts({ components, onApply }) {
  const [mode, setMode] = useState(null); // null | "suggest" | "paste"
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  // Suggest state
  const [ctc, setCtc] = useState(1800000);
  const [role, setRole] = useState("");
  const [location, setLocation] = useState("");
  const [regime, setRegime] = useState("new");
  const [notes, setNotes] = useState("");
  // Paste state
  const [pasted, setPasted] = useState("");
  const [pasteCtc, setPasteCtc] = useState("");

  const runSuggest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/ai/suggest-structure", {
        target_ctc_annual: Number(ctc) || 0,
        role, location, tax_regime: regime, include_pf: true, include_gratuity: true,
        notes,
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "AI could not draft the structure.");
    } finally { setBusy(false); }
  };
  const runParse = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/ai/parse-breakdown", {
        text: pasted, target_ctc_annual: Number(pasteCtc) || undefined,
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "AI could not parse the breakdown.");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-amber-200 bg-amber-50/50 rounded-md p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <Sparkle size={16} weight="fill" className="text-amber-500"/>
          <b>AI shortcuts</b>
          <span className="text-zinc-500 text-xs">— skip the manual setup and let AI draft it.</span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant={mode==="suggest"?"default":"outline"} className="gap-1.5 h-8"
                  onClick={() => { setMode("suggest"); setPreview(null); }} data-testid="ai-suggest-btn">
            <Sparkle size={12}/> Suggest by role
          </Button>
          <Button size="sm" variant={mode==="paste"?"default":"outline"} className="gap-1.5 h-8"
                  onClick={() => { setMode("paste"); setPreview(null); }} data-testid="ai-paste-btn">
            <ClipboardText size={12}/> Paste breakdown
          </Button>
          {mode && (
            <Button size="sm" variant="ghost" className="h-8" onClick={() => { setMode(null); setPreview(null); }}>
              Cancel
            </Button>
          )}
        </div>
      </div>

      {mode === "suggest" && !preview && (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">CTC (₹/yr)</Label>
              <Input type="number" value={ctc} onChange={e=>setCtc(e.target.value)} data-testid="ai-ctc"/>
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Input placeholder="Senior Engineer" value={role} onChange={e=>setRole(e.target.value)} data-testid="ai-role"/>
            </div>
            <div>
              <Label className="text-xs">Location</Label>
              <Input placeholder="Bangalore" value={location} onChange={e=>setLocation(e.target.value)} data-testid="ai-loc"/>
            </div>
            <div>
              <Label className="text-xs">Tax regime</Label>
              <Select value={regime} onValueChange={setRegime}>
                <SelectTrigger className="h-9"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New regime</SelectItem>
                  <SelectItem value="old">Old regime</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes for AI (optional)</Label>
            <Input placeholder={`e.g. "Include meal card ₹2,200/mo, WFH allowance ₹1,500"`} value={notes} onChange={e=>setNotes(e.target.value)}/>
          </div>
          <Button size="sm" onClick={runSuggest} disabled={busy} className="gap-1.5" data-testid="ai-suggest-run">
            <Sparkle size={12} weight="bold"/>{busy ? "Drafting…" : "Draft with AI"}
          </Button>
        </div>
      )}

      {mode === "paste" && !preview && (
        <div className="mt-3 space-y-2">
          <div>
            <Label className="text-xs">Paste the breakdown (Excel copy, annexure text, offer letter table…)</Label>
            <Textarea rows={6} value={pasted} onChange={e=>setPasted(e.target.value)} data-testid="ai-paste-text"
              placeholder={`Basic\t60000\nHRA\t30000\nSpecial Allowance\t45000\nConveyance\t1600\nPF\t1800\nPT\t200`}/>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">CTC hint (optional)</Label>
              <Input type="number" placeholder="1800000" value={pasteCtc} onChange={e=>setPasteCtc(e.target.value)} data-testid="ai-paste-ctc"/>
            </div>
          </div>
          <Button size="sm" onClick={runParse} disabled={busy || !pasted.trim()} className="gap-1.5" data-testid="ai-paste-run">
            <ClipboardText size={12} weight="bold"/>{busy ? "Parsing…" : "Parse with AI"}
          </Button>
        </div>
      )}

      {preview && (
        <div className="mt-3 space-y-2 border-t border-amber-200 pt-3">
          <div className="text-xs text-zinc-700"><b>AI proposal:</b> {preview.name || "Custom structure"} · {preview.lines?.length || 0} component{(preview.lines?.length || 0) === 1 ? "" : "s"} · CTC ₹{Number(preview.target_ctc_annual || 0).toLocaleString("en-IN")}</div>
          {preview.ai_notes && <div className="text-xs bg-white border border-zinc-200 rounded p-2 text-zinc-600">{preview.ai_notes}</div>}
          <div className="text-xs max-h-[130px] overflow-y-auto border border-zinc-200 rounded bg-white p-2 space-y-0.5">
            {(preview.lines || []).map((l,i) => (
              <div key={i} className="flex justify-between font-mono text-[11px]">
                <span>{l.component_code} <span className="text-zinc-400">({l.calculation_type})</span></span>
                <span>{l.value}</span>
              </div>
            ))}
            {(preview.lines || []).length === 0 && <div className="text-zinc-500 italic">No components mapped. Try adjusting the input.</div>}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => { onApply(preview); setPreview(null); setMode(null); }} className="gap-1.5" data-testid="ai-apply-btn" disabled={!preview.lines?.length}>
              Apply to form
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>Try again</Button>
          </div>
        </div>
      )}
    </div>
  );
}
